// Shared between `OverviewRail` and `CustomizeHouseholdViewDialog` so the dialog's Overview pane
// can seed its draft from the same key set, default order, titles and icons the rail itself uses.

import type { LucideIcon } from 'lucide-react';
import { SavvyIntelligenceMark } from '@savvy-kit/foundations/savvy-intelligence-mark';
import { cn } from '@savvy-kit/lib/utils';
import { House, PieChart, Users } from 'lucide-react';

export const RELATIONSHIP_PANEL_KEY = 'relationship';
export const HOUSEHOLD_PANEL_KEY = 'household';
export const CONTACTS_PANEL_KEY = 'contacts';
export const PORTFOLIO_PANEL_KEY = 'portfolio';

// Grayscaled so the Savvy Intelligence mark sits alongside the other gray section icons instead of
// reading as a badge.
export function RelationshipPanelIcon({ className }: { readonly className?: string }) {
  return <SavvyIntelligenceMark size="sm" className={cn('grayscale opacity-70 [&_svg]:size-[18px]', className)} />;
}

type OverviewSectionMeta = {
  readonly title: string;
  /** Second line in the Customize dialog's Overview pane; the rail shows the title alone. */
  readonly description: string;
  readonly icon: LucideIcon | typeof RelationshipPanelIcon;
};

export const OVERVIEW_SECTION_META = {
  [RELATIONSHIP_PANEL_KEY]: {
    title: 'Relationship',
    description: 'Savvy Intelligence relationship summary',
    icon: RelationshipPanelIcon,
  },
  [HOUSEHOLD_PANEL_KEY]: {
    title: 'Household',
    description: 'Summary, members, and beneficiaries',
    icon: House,
  },
  [CONTACTS_PANEL_KEY]: {
    title: 'Contacts',
    description: 'Household and professional contacts',
    icon: Users,
  },
  [PORTFOLIO_PANEL_KEY]: {
    title: 'Portfolio',
    description: 'YTD performance and allocation',
    icon: PieChart,
  },
} satisfies Record<string, OverviewSectionMeta>;

// A saved order can name a key no section claims any more, so the dialog looks its rows up through
// a Map — the object above stays literally keyed for the rail, which asks for one section at a time.
const META_BY_KEY = new Map<string, OverviewSectionMeta>(Object.entries(OVERVIEW_SECTION_META));

export function overviewSectionMeta(key: string): OverviewSectionMeta | undefined {
  return META_BY_KEY.get(key);
}

// Names the Relationship panel even though it is absent while its full view is open, so a reorder
// made then leaves it the slot it comes back to.
export const DEFAULT_OVERVIEW_ORDER: readonly string[] = [
  RELATIONSHIP_PANEL_KEY,
  HOUSEHOLD_PANEL_KEY,
  CONTACTS_PANEL_KEY,
  PORTFOLIO_PANEL_KEY,
];

/** Relationship is permanent — it never appears here, so it never counts toward the dialog's floor. */
export const HIDEABLE_OVERVIEW_SECTION_KEYS: readonly string[] = [
  HOUSEHOLD_PANEL_KEY,
  CONTACTS_PANEL_KEY,
  PORTFOLIO_PANEL_KEY,
];
